/* src/components/pages/Estadisticacesionesdeu.jsx */
/* ----- estadística de cesiones por Deudor ------ */

import React, { useEffect, useState, useCallback } from "react";

import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import NoData from "./NoData";

const meses = [
  "",
  "Ene","Feb","Mar","Abr","May","Jun",
  "Jul","Ago","Sep","Oct","Nov","Dic"
];

/* ================== Card reutilizable ================== */
const ChartCard = ({ title, subtitle, data, color }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  return (
    <Card
      sx={{
        flex: {
          xs: "1 1 100%",
          sm: "1 1 calc(50% - 16px)",
          md: "1 1 calc(33.333% - 16px)",
        },
        maxWidth: {
          xs: "100%",
          sm: "calc(50% - 16px)",
          md: "calc(33.333% - 16px)",
        },
        minWidth: 280,
        height: 260,
        display: "flex",
        flexDirection: "column",
        borderTop: `3px solid ${ui.titleAccent}`,
      }}
    >
      <CardContent  sx={{ pb: 1 }}>
        <Typography sx={ui.cardTitleAccent}>{title}</Typography>
        <Typography sx={ui.cardSubtitle}>{subtitle}</Typography>

        {data?.length ? (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={data}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={ui.chartGrid}
                vertical={false}
              />

              <XAxis
                dataKey="mes"
                tickFormatter={(m) => meses[m]}
                tick={{ fill: ui.chartTick, fontSize: 12 }}
              />

              <YAxis
                tick={{ fill: ui.chartTick, fontSize: 12 }}
                tickFormatter={(v) =>
                  new Intl.NumberFormat("es-CL", {
                    notation: "compact",
                  }).format(v)
                }
              />

              <Tooltip
                labelFormatter={(l) => `Mes: ${meses[l]}`}
                formatter={(v) =>
                  `$${Number(v).toLocaleString("es-CL")}`
                }
              />

              <Line
                type="monotone"
                dataKey="total"
                stroke={color}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              height: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoData mensaje="No hay datos" />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/* ================== Componente principal ================== */
const Estadisticacesionesdeu = () => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();
  const theme = useTheme();
  const ui = theme.custom.ui;

  const [anos, setAnos] = useState([]);
  const [anoSeleccionado, setAnoSeleccionado] = useState("");
  const [payload, setPayload] = useState(null);

  /* ===== Años ===== */
  const fetchAnos = useCallback(async () => {
    try {
      const res = await api.post("/cesiones/getanoscesiones", {
        RutCli: user.usuario,
        Tipocesion: "DEUDOR",
      });

      if (res.data?.rows?.length) {
        setAnos(res.data.rows);
        setAnoSeleccionado(res.data.rows[0].ano);
      }

    } catch {
      showFeedback({
        message: "No fue posible obtener los años",
        severity: "error",
      });
    }
  }, [user.usuario, showFeedback]);

  useEffect(() => {
    fetchAnos();
  }, [fetchAnos]);

  /* ===== Data ===== */
  const fetchData = useCallback(async () => {
    if (!anoSeleccionado) return;

    try {
      const res = await api.post("/cesiones/getestadisticaCesiones", {
        RutCli: user.usuario,
        Anoaconsultar: anoSeleccionado,
        Tipocesion: "DEUDOR",
      });

      if (res.data?.success) {
        setPayload(res.data);
      }

    } catch {
      showFeedback({
        message: "No fue posible obtener estadísticas",
        severity: "error",
      });
    }
  }, [anoSeleccionado, user.usuario, showFeedback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!payload) return null;

  const { series, resumen } = payload;

  return (
    <Box>

      {/* ===== Controles ===== */}
      <Stack direction="row" spacing={1} mb={1}>
        <TextField
          label="Rut"
          value={user.usuario}
          size="small"
          disabled
          sx={ui.readonlyField}
        />

        <TextField
          label="Cliente"
          value={user.nombre}
          size="small"
          disabled
          sx={{ ...ui.readonlyField, flexGrow: 1 }}
        />

        <TextField
          select
          label="Año"
          value={anoSeleccionado}
          onChange={(e) => setAnoSeleccionado(e.target.value)}
          size="small"
          sx={{ width: 100 }}
        >
          {anos.map(({ ano }) => (
            <MenuItem key={ano} value={ano}>
              {ano}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* ===== Cards ===== */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "stretch",
        }}
      >

        <ChartCard title="Cesiones Año Actual" subtitle={`Año ${anoSeleccionado}`} data={series.actual} color={ui.primary} />

        <ChartCard title="Cesiones Año Anterior" subtitle={`Año ${anoSeleccionado - 1}`} data={series.anterior} color={ui.secondary} />

        <ChartCard title="Pendientes" subtitle={`Año ${anoSeleccionado}`} data={series.pendientes} color={ui.warning} />

        <ChartCard title="En Nómina" subtitle={`Año ${anoSeleccionado}`} data={series.nomina} color={ui.info} />

        <ChartCard title="Pagadas" subtitle={`Año ${anoSeleccionado}`} data={series.pagadas} color={ui.success} />

        {/* ===== Resumen ===== */}
        <Card
          sx={{
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 16px)",
              md: "1 1 calc(33.333% - 16px)",
            },
            maxWidth: {
              xs: "100%",
              sm: "calc(50% - 16px)",
              md: "calc(33.333% - 16px)",
            },
            minWidth: 280,
            borderTop: `3px solid ${ui.titleAccent}`,
          }}
        >
          <CardContent>
            <Typography sx={ui.cardTitleAccent}>
              Resumen de Cesiones
            </Typography>

            <Typography sx={ui.cardSubtitle}>
              Año {anoSeleccionado}
            </Typography>

            <Stack spacing={1.2} mt={2}>

              <Row label="Año Actual" value={resumen.totalActual} />
              <Row label="Año Anterior" value={resumen.totalAnterior} />
              <Row label="Pendientes" value={resumen.totalPendiente} />
              <Row label="En Nómina" value={resumen.totalNomina} />
              <Row label="Pagadas" value={resumen.totalPagado} />

            </Stack>
          </CardContent>
        </Card>

      </Box>
    </Box>
  );
};

/* ===== Helper fila resumen ===== */
const Row = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography color="text.secondary">{label}</Typography>
    <Typography sx={{ fontWeight: 600 }}>
      ${value.toLocaleString("es-CL")}
    </Typography>
  </Stack>
);

export default Estadisticacesionesdeu;
